import { type Dispatch, memo, type SetStateAction } from 'react';
import { isMobile } from 'react-device-detect';
import classNames from 'classnames';
import styles from './ButtonTogglePair.module.css';

type ToggleButtonProps = {
  label: string | number;
  onClick: () => void;
  isSelected: boolean;
};

const ToggleButton = memo(
  ({ label, onClick, isSelected }: ToggleButtonProps) => {
    return (
      <button
        style={isMobile ? { fontSize: '0.8em' } : {}}
        className={classNames([styles.button, isSelected && styles.selected])}
        onClick={onClick}
      >
        {label}
      </button>
    );
  }
);

type Option = {
  label: string | number;
  value: string | number;
};

type Props<T> = {
  options: Array<Option>;
  value: T;
  setValue: Dispatch<SetStateAction<T>>;
  valueToUpdate: keyof T;
  getNewDBData?: boolean;
};

export default function ButtonTogglePair<T>({
  options,
  value,
  setValue,
  valueToUpdate,
  getNewDBData,
}: Props<T>) {
  // useCallback = 'remember' a function
  // useMemo = 'remember' a value
  // memo = wrap a function/component in it, and it wont re-render if props = same (shallow)

  const handleClick = (clickedValue: string | number) => {
    if (getNewDBData) {
      setValue((v) => ({
        ...v,
        // Computed-key spread over a generic widens the inferred type; cast
        // back to T (runtime shape is unchanged).
        [valueToUpdate]: clickedValue,
      }) as T);
    } else {
      setValue((v) => ({
        ...v,
        [valueToUpdate]: clickedValue,
      }) as T);
    }
  };

  return (
    <div className={styles.buttonPair}>
      {options.map(({ label, value: optionValue }, index) => (
        <ToggleButton
          key={`option_${index}`}
          label={label}
          onClick={() => handleClick(optionValue)}
          isSelected={(value[valueToUpdate] as unknown) === optionValue}
        />
      ))}
    </div>
  );
}
